//* 주문 배송지(수령 주소) 수정 모달 — 호실 이동, 주소 오기입 교정
//* patch-detail dot-notation 부분 업데이트 — 중첩 객체({ deliveryDetail: {...} })로 보내면
//* mongoose가 deliveryDetail 서브문서를 통째로 교체해 배송상태·수령인이 날아가므로 금지.

import { FC, useCallback, useEffect, useMemo } from 'react';

import { useRecoilState, useRecoilValue } from 'recoil';

import { updateShopOrder } from '@shared/api/shopOrder/shopOrder.controller';
import { shopOrderListForSellerUrl } from '@shared/api/shopOrder/shopOrder.hook';
import { ShopOrderForSeller } from '@shared/api/shopOrder/shopOrder.interface';
import {
  feventAddressMismatch,
  feventCurrentAddress,
} from '@shared/api/shopOrder/shopOrder.utils';
import { Button } from '@shared/components/Button';
import Modal from '@shared/components/Modal';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';

interface Props {
  openId: string;
  order: ShopOrderForSeller;
}

interface AddressForm {
  receiverAddress: string;
}

export const OrderAddressEditModal: FC<Props> = function OrderAddressEditModal({
  openId,
  order,
}: Props) {
  const { sellerRole } = useAuth();
  const [open, setOpen] = useRecoilState(simpleOpenAtomFamily(openId));
  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);
  const queryClient = useQueryClient();

  const onCloseModal = useCallback(() => {
    setOpen(false);
    history.back();
  }, [setOpen]);

  const { register, handleSubmit, reset, setValue } = useForm<AddressForm>({
    defaultValues: {
      receiverAddress: order.deliveryDetail.receiverAddress,
    },
  });

  //* 부고(fevent) 기준 현재 상가 주소 — 호실이동으로 fevent가 갱신됐다면 여기에 새 호실이 온다.
  const feventAddress = useMemo(() => feventCurrentAddress(order), [order]);
  const feventAddressDiffers = feventAddressMismatch(order);

  //* 열리는 순간에만 최신 주소로 리시드 (편집 중 refetch로 인한 입력값 클로버 방지 — order를 deps에 넣지 않음)
  useEffect(() => {
    if (open) {
      reset({
        receiverAddress: order.deliveryDetail.receiverAddress,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reset]);

  const onSubmit: SubmitHandler<AddressForm> = useCallback(
    async (data) => {
      const receiverAddress = data.receiverAddress.trim();
      if (!receiverAddress) {
        notificateError(notifiacationInstance, '배송지 주소를 입력해주세요.');
        return;
      }
      const orderPatch = {
        _id: order._id,
        'deliveryDetail.receiverAddress': receiverAddress,
      };

      try {
        await updateShopOrder(orderPatch, sellerRole());
        queryClient.invalidateQueries(shopOrderListForSellerUrl(sellerRole()));
        notificateSuccess(notifiacationInstance, '배송지 주소 수정 완료');
        onCloseModal();
      } catch (err) {
        console.error(err);
        notificateError(notifiacationInstance, '배송지 주소 수정에 실패했습니다.');
      }
    },
    [notifiacationInstance, onCloseModal, order._id, queryClient, sellerRole],
  );

  return (
    <Modal openId={openId}>
      <div className="space-y-4">
        <div>
          <p className="text-lg font-bold">배송지 주소 수정</p>
          <p className="break-keep pt-1 text-sm text-gray-600">
            호실 이동, 주소 오기입 시 수령 주소 표기를 교정합니다. 배송상태·수령인 정보는
            변경되지 않으며 고객에게 알림이 발송되지 않습니다.
          </p>
        </div>
        {feventAddress && (
          <div>
            <p className="pb-1 text-sm font-medium text-gray-600">
              부고 기준 현재 상가 주소
            </p>
            <div className="flex items-start justify-between gap-2 rounded-md border bg-gray-50 p-2">
              <p className="whitespace-pre-wrap break-keep text-sm text-gray-700">
                {feventAddress}
              </p>
              <Button
                className="shrink-0 shadow-sm"
                onClick={() => {
                  setValue('receiverAddress', feventAddress, { shouldDirty: true });
                }}
              >
                적용
              </Button>
            </div>
            {feventAddressDiffers && (
              <p className="break-keep pt-1 text-xs text-amber-600">
                주문에 저장된 배송지 주소와 다릅니다. 호실 이동 등으로 부고가 갱신된 경우
                [적용]을 눌러 반영하세요.
              </p>
            )}
          </div>
        )}
        <div className="font-bold">
          <p className="pb-1 text-sm font-medium text-gray-600">배송지 주소</p>
          <textarea
            className="expandable-textarea w-full"
            rows={3}
            placeholder="OO장례식장 별관 302호"
            {...register('receiverAddress', { required: true })}
          />
        </div>
        <Button
          className="button-rectangle h-10 theme-text-4 theme-bg-main"
          onClick={handleSubmit(onSubmit)}
        >
          수정
        </Button>
      </div>
    </Modal>
  );
};
